import { env } from "environment-interface/env";
import { withBasePath } from "isomorphic/utils";

/**
 * Base path + scheme (https)
 */
export const withBasePathAndScheme = (url: string): string => {
  return `https://${withBasePath(env, url)}`;
};

export const appendBasePath = (url: string): string => {
  const basePath = env.PUBLIC_BASE_PATH;

  if (basePath) return `${url}${basePath}`;

  return url;
};

export const withPitcherUrlProtocol = (url: string): string => {
  if (url.startsWith("http")) {
    return url;
  }

  // Split away the protocol (http: or https:)
  const protocol = env.PUBLIC_PITCHER_MANAGER_URL.split(":")[0];
  return `${protocol}://${url}`;
};

/**
 * Removes a leading slash from the given path.
 */
export const withoutLeadingSlash = (path: string): string => {
  return path.replace(/^\//, "");
};

export const isUrl = (str: string): boolean => {
  try {
    new URL(str);
    return true;
  } catch {
    return false;
  }
};

export class UnknownProtocolError extends Error {
  constructor(public readonly protocol: string) {
    super(`Unknown protocol: ${protocol}`);
  }
}

/**
 * Attempts to open a given URL with a custom protocol
 * (i.e. `vscode-insiders://`). Resolves if the protocol has a handler
 * registered in the system. Rejects if the protocol is unknown.
 *
 * @example
 * openCustomProtocol('vscode-insiders://path-here')
 */
export function openCustomProtocol(url: URL): Promise<void> {
  const checkTimeoutMs = 1000;

  const protocolIsRegistered = new Promise<void>((resolve) => {
    window.addEventListener("blur", () => resolve(), { once: true });
  });
  const protocolIsUnknown = new Promise<void>((_, reject) => {
    setTimeout(
      () => reject(new UnknownProtocolError(url.protocol)),
      checkTimeoutMs,
    );
  });

  window.location.assign(url);

  /**
   * @note There is no native way of checking if a protocol has
   * a registered handler in the system. Instead, rely on whether
   * the main window has lost focus as that likely indicates
   * a "Open in {APP_NAME}" model has appeared and stole the focus.
   */
  return Promise.race([protocolIsRegistered, protocolIsUnknown]);
}
